/**
 * Shared fetch for the Helm unread-count endpoint
 * (`GET /organizations/:orgId/helm/unread-count`).
 *
 * Spec 294 category 3 follow-up (2026-08-03) — fixes a query-key/shape
 * collision: `useLayout.ts` (header badge) and `useHelmReadStatus.ts`
 * (dashboard section breakdown) both register a `useQuery` observer on
 * `queryKeys.helm.unreadCount(orgId)`. They hit the exact same backend
 * route with the exact same param, so a shared cache key is correct and
 * desirable (one network request serves both when mounted together, which
 * they are — `HelmDataProvider` wraps `Layout`/`PillarLayout` on
 * `HelmPage`). The bug was that each hook had its OWN `queryFn` unwrapping
 * the response into a different shape (`useLayout.ts` kept only `.total`
 * as a bare number; `useHelmReadStatus.ts` kept the full breakdown object)
 * — TanStack Query doesn't support two different `queryFn`s sharing one
 * cache key, so whichever observer's options applied at fetch time won for
 * BOTH, silently corrupting the other's consumers. Fix: one `queryFn`,
 * one shape (the full `UnreadCounts` object), used by both hooks.
 * `useLayout.ts` reads `.total` off of it for the header badge;
 * `useHelmReadStatus.ts` exposes the full breakdown for section badges.
 */

import { helmAPI } from '../../services/api';
import { unwrapResponse } from '../../utils/unwrapResponse';

export interface UnreadCounts {
  tasks: number;
  approvals: number;
  helpRequests: number;
  activity: number;
  total: number;
}

export const DEFAULT_UNREAD_COUNTS: UnreadCounts = {
  tasks: 0,
  approvals: 0,
  helpRequests: 0,
  activity: 0,
  total: 0,
};

export async function fetchHelmUnreadCounts(
  organizationId: string,
  signal?: AbortSignal
): Promise<UnreadCounts> {
  const response = await helmAPI.getUnreadCount(organizationId, { signal });
  return unwrapResponse<any>(response.data);
}
